"use client";

import { useRef, useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { t } from "i18next";
import AudioRecorderUI from "../audio-recorder";
import RecorderForm, {
  type RecorderFormHandle,
} from "../audio-recorder/recorder-form";
import { useVisitsStore } from "@/store/visits.store";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth.store";
import getDuration from "@/lib/getDuration";
import { getBlobFromUrl } from "@/lib/getBlobFromUrl";
import RecorderAudioPlayer from "../audio-recorder/recorder-audio-player";
import ConfirmDialog from "../ConfirmDialog";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import { Button } from "@/components/ui/button";
import { MicrophoneButton } from "@/components/ui/MicrophoneButton";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import {
  MicDeviceType,
  ValidationSuscriptionButton,
} from "@/common/enums/enums";
import { useBackgroundVisitUploadStore } from "@/store/backgroundVisitUpload.store";
import { useAudioValidation } from "@/hooks/useAudioValidation";
import { AlertMessage } from "../AlertMessage";
import { UploadAudioButton } from "./UploadRecordButton";
import { ALLOWED_AUDIO_MIME_TYPES } from "@/common/config";
import { useVisitSubmit } from "@/hooks/visits/useVisitSubmit";
import { v4 as uuidv4 } from "uuid";
import { Separator } from "@/components/ui/separator";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { AdvancedSettings } from "./AdvancedSettings";
import { SwitchTranscriptionRealtime } from "../SwitchTranscriptionRealtime";
import { SyncRecordingSheet } from "@/components/ui/visits/SyncRecordingSheet";
import { useCreateLinkedRecordingSession } from "@/hooks/linkedRecordingSession/useCreateLinkedRecordingSession";

export function NewVisitSidebar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { toast } = useToast();
  const [showRecorder, setShowRecorder] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [transcription, setTranscription] = useState<string | null>(null);
  const recorderFormRef = useRef<RecorderFormHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { loadingUploadFiles, errorUploadFiles, getFiles } = useVisitsStore();
  const { userData } = useAuthStore.getState();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();
  const { error, validateFile, resetError } = useAudioValidation();
  const alreadyCompletedRef = useRef<boolean>(false);
  const { enqueueVisitToUpload } = useBackgroundVisitUploadStore.getState();
  const { submitVisit, loading: submittingVisit } = useVisitSubmit();
  const {
    inputLanguage,
    isRealtimeEnabled,
    micDevice,
    transcriptionNoRealtimeProvider,
    diarize,
  } = useRecordingSettingsStore();
  const {
    createLinkedRecordingSession,
    result: micSyncRequest,
    loading: micSyncRequestLoading,
    reset: micSyncRequestReset,
  } = useCreateLinkedRecordingSession();
  const [showStartRecordingDialog, setShowStartRecordingDialog] =
    useState(false);

  const isCreatingVisit = submittingVisit || loadingUploadFiles;

  const handlePreventCloseOnInteraction = (event: Event): void => {
    if (
      showRecorder ||
      isFileUpload ||
      submitting ||
      showStartRecordingDialog
    ) {
      event.preventDefault();
    }
  };

  const handleRecordingComplete = (
    blobUrl: string,
    transcription: string
  ): void => {
    if (alreadyCompletedRef.current) return;
    alreadyCompletedRef.current = true;
    setRecordedBlobUrl(blobUrl);
    setAudioFinished(true);
    setTranscription(transcription);
    if (userData) void fetchAndSetAvailableMinutes(userData.id);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (file && validateFile(file)) {
      const fileUrl = URL.createObjectURL(file);
      setRecordedBlobUrl(fileUrl);
      setAudioFinished(true);
      setIsFileUpload(true);
      if (userData) void fetchAndSetAvailableMinutes(userData.id);
    }
  };

  const handleLegacyFormSubmit = async (
    formData: RecorderFormSchemaType
  ): Promise<void> => {
    if (!recordedBlobUrl) return;

    try {
      const audioBlob = await getBlobFromUrl(recordedBlobUrl);
      const duration = await getDuration(audioBlob);
      if (userData?.id) {
        await enqueueVisitToUpload({
          file: audioBlob,
          userId: userData.id,
          duration,
          language: inputLanguage,
          style: formData.style,
          format: formData.format,
          exam: formData.examination,
          template: formData.templateName,
          macros: "",
          customTemplateId: formData.customTemplateId,
          audioTranscriptionServiceAiId: transcriptionNoRealtimeProvider,
          diarize: diarize,
        });
        setSubmitting(true);
      } else {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: t("visits.sidebar.messages.requestFailed.description"),
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description:
          error instanceof Error
            ? error.message
            : t("visits.sidebar.messages.requestFailed.description"),
      });
    }
  };

  const handleRealtimeFormSubmit = async (
    formData: RecorderFormSchemaType
  ): Promise<void> => {
    if (!recordedBlobUrl || !userData?.id) return;
    if (!transcription) {
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description: t("visits.sidebar.messages.noTranscription"),
      });
      return;
    }
    try {
      const audioBlob = await getBlobFromUrl(recordedBlobUrl);
      const duration = await getDuration(audioBlob);
      await submitVisit(
        {
          user: userData?.id.toString() ?? "",
          name: uuidv4(),
          duration,
          style: formData.style,
          format: formData.format,
          exam: formData.examination,
          template: formData.templateName,
          customTemplate: formData.customTemplateId ?? undefined,
          language: inputLanguage,
          transcription,
        },
        audioBlob
      );
      setSubmitting(true);
    } catch (error) {
      console.error(error);
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description:
          error instanceof Error
            ? error.message
            : t("visits.sidebar.messages.requestFailed.description"),
      });
    }
  };

  const handleFormSubmit = async (
    formData: RecorderFormSchemaType
  ): Promise<void> => {
    if (isRealtimeEnabled) {
      await handleRealtimeFormSubmit(formData);
    } else {
      await handleLegacyFormSubmit(formData);
    }
  };

  const handleRecordAudio = (): void => {
    if (micDevice?.type == MicDeviceType.MOBILE) {
      setShowStartRecordingDialog(true);
    } else {
      setShowRecorder(true);
    }
  };

  useEffect(() => {
    if (submitting && !isCreatingVisit) {
      if (!errorUploadFiles) {
        toast({ description: t("visits.sidebar.messages.fileEnqueued") });
        onOpenChange(false);
      } else {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: t("visits.sidebar.messages.requestFailed.description"),
        });
      }
      setSubmitting(false);
    }
  }, [
    isCreatingVisit,
    submitting,
    errorUploadFiles,
    onOpenChange,
    toast,
    getFiles,
    userData?.id,
  ]);

  const resetStates = (): void => {
    setShowRecorder(false);
    setIsFileUpload(false);
    setAudioFinished(false);
    setRecordedBlobUrl(null);
    setSubmitting(false);
    alreadyCompletedRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!open) {
      resetStates();
      resetError();
      micSyncRequestReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetError]);

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!isCreatingVisit) {
          onOpenChange(isOpen);
        }
      }}
    >
      {micSyncRequest ? (
        <SyncRecordingSheet
          linkedRecordingSession={micSyncRequest.data}
          open={open}
          onOpenChange={onOpenChange}
        />
      ) : (
        <SheetContent
          className="w-full sm:max-w-md p-0 sm:rounded-l-2xl"
          onEscapeKeyDown={handlePreventCloseOnInteraction}
          onInteractOutside={handlePreventCloseOnInteraction}
        >
          <SheetTitle></SheetTitle>
          <SheetDescription></SheetDescription>
          <SheetHeader />
          <input
            ref={fileInputRef}
            accept={ALLOWED_AUDIO_MIME_TYPES.join(",")}
            style={{ display: "none" }}
            type="file"
            onChange={handleFileChange}
          />
          <div className="flex flex-col flex-1 h-full">
            <div className="flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10">
              {isFileUpload ? (
                recordedBlobUrl && (
                  <RecorderAudioPlayer mediaBlobUrl={recordedBlobUrl} />
                )
              ) : showRecorder ? (
                <AudioRecorderUI
                  onFinished={setAudioFinished}
                  onRecordingComplete={handleRecordingComplete}
                />
              ) : (
                <>
                  <MicrophoneButton
                    onClick={() => {
                      setShowRecorder(true);
                    }}
                  />
                  <div className="flex flex-col w-full gap-4">
                    <AdvancedSettings />
                    <Button
                      className="w-full"
                      disabled={micSyncRequestLoading}
                      size="xl"
                      onClick={handleRecordAudio}
                    >
                      {t("visits.sidebar.recordAudio")}
                    </Button>
                    <SwitchTranscriptionRealtime />
                  </div>
                  <Separator />
                  <UploadAudioButton
                    disabled={isRealtimeEnabled}
                    onClick={() => fileInputRef.current?.click()}
                  />
                  {error && (
                    <AlertMessage
                      description={error}
                      title={t("error.formRecorder.titleMessage")}
                      variant="destructive"
                    />
                  )}
                </>
              )}
              {audioFinished && (
                <RecorderForm
                  ref={recorderFormRef}
                  onSubmitData={handleFormSubmit}
                />
              )}
            </div>
            <SheetFooter className="bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2">
              {audioFinished && (
                <SubscriptionOnlyButton
                  className="w-full"
                  disabled={isCreatingVisit || micSyncRequestLoading}
                  size={"xl"}
                  validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
                  onClick={async () => {
                    await recorderFormRef.current?.submitForm();
                  }}
                >
                  {isCreatingVisit
                    ? `${t("visits.sidebar.processing")}...`
                    : t("visits.sidebar.submit")}
                </SubscriptionOnlyButton>
              )}
              <ConfirmDialog
                description={t("visits.sidebar.cancelConfirmation.description")}
                open={showCancelConfirmation}
                title={t("visits.sidebar.cancelConfirmation.title")}
                trigger={
                  <Button
                    className="w-full"
                    disabled={isCreatingVisit}
                    size={"xl"}
                    variant={"grey"}
                  >
                    {t("visits.sidebar.cancel")}
                  </Button>
                }
                onOpenChange={setShowCancelConfirmation}
                onConfirm={() => {
                  if (!isCreatingVisit) onOpenChange(false);
                }}
              />
            </SheetFooter>
          </div>
        </SheetContent>
      )}
      <ConfirmDialog
        cancelText="No"
        confirmText="Yes"
        open={showStartRecordingDialog}
        title={"Use this microphone"}
        description={
          "Would you like to use this microphone device as the micrphone input?"
        }
        onOpenChange={setShowStartRecordingDialog}
        onConfirm={() => {
          if (micDevice?.type == MicDeviceType.MOBILE && userData?.id) {
            void createLinkedRecordingSession(userData.id, micDevice.value);
          }
        }}
      />
    </Sheet>
  );
}
